import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    IconPlus, IconX, IconPrinter, IconDeviceFloppy, IconDownload,
    IconFileInvoice, IconPencil, IconTrash, IconArrowLeft, IconAlertTriangle,
    IconUserSearch, IconCheck, IconWallet, IconCopy,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import invoiceService from '../../../services/invoiceService';
import sohozJobCustomerService from '../../../services/sohozJobCustomerService';
import incomeExpenseService from '../../../services/incomeExpenseService';
import questionPaperService from '../../../services/questionPaperService';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { buildInvoiceHTML, buildPaginatedInvoiceHTML, DEFAULT_INVOICE_TEMPLATE, paperPx, getInvoiceFontEmbedCSS, invoiceDeposit, customerPreviousDue } from '../../../lib/invoiceTemplates';
import InvoicePreviewFrame from '../../../components/invoice/InvoicePreviewFrame';
import InvoiceSettings from '../../../components/invoice/InvoiceSettings';
import GeoCombobox from '../../../components/common/GeoCombobox';
import DatePickerField from '../../../components/common/DatePickerField';
import { buildShopServiceList } from '../../../lib/incomeExpenseDefaults';
import { copyImageToClipboard } from '../../../utils/imageShare';
import QRCode from 'qrcode';

// Build a QR data-URL encoding the customer's name + phone (each on its own line).
// Returns '' when both are empty so no QR is rendered.
async function buildQrDataUrl(name, phone) {
    const text = [String(name || '').trim(), String(phone || '').trim()].filter(Boolean).join('\n');
    if (!text) return '';
    try {
        return await QRCode.toDataURL(text, { margin: 1, width: 220, errorCorrectionLevel: 'M' });
    } catch {
        return '';
    }
}

// ── Bengali digit helpers ────────────────────────────────────────────────────
const BN = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const toBn = s => String(s).replace(/[0-9]/g, d => BN[d]);
const toEn = s => String(s).replace(/[০-৯]/g, d => BN.indexOf(d));
const parseNum = s => Number(toEn(s)) || 0;
// A blank or 0 quantity still bills one unit — treat it as 1 so entering 0
// (or leaving it empty) never zeroes the line total.
const qtyOf = q => parseNum(q) || 1;
// Strip tag delimiters & control chars from free-text inputs so nothing dangerous
// (e.g. <script>) is ever stored. The invoice HTML also escapes every value on
// render, so this is defense in depth.
const sanitizeText = v => String(v ?? '').replace(/[<>]/g, '').replace(/[\x00-\x1F\x7F]/g, '');
function fmtAmount(num) {
    const n = parseNum(num);
    if (!n && n !== 0) return toBn('0');
    const formatted = n.toLocaleString('en-BD', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return toBn(formatted);
}

// ── Generate random invoice number ──────────────────────────────────────────
// The prefix is the shop's initials — first letter of each word ("Akram Computer"
// → "AC", "মা টেলিকম সেন্টার" → "মটস") — capped at 4 chars; falls back to INV.
function shopPrefix(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return 'INV';
    return words.map((w) => [...w][0]).join('').toUpperCase().slice(0, 4);
}
function genInvoiceNo(prefix) {
    return `${prefix || 'INV'}-` + Math.floor(1000 + Math.random() * 9000);
}

// ── Bengali date ─────────────────────────────────────────────────────────────
const BD_TZ = 'Asia/Dhaka';
function getBnDate(d = new Date()) {
    return new Date(d).toLocaleDateString('bn-BD', { timeZone: BD_TZ, year: 'numeric', month: 'long', day: 'numeric' });
}
// A date as YYYY-MM-DD in Dhaka time — the value shape used by <input type="date">.
function toDateInput(d = new Date()) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: BD_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d));
}
// A YYYY-MM-DD string → Bengali long date. Parsed at noon so the day never shifts
// across timezones when formatted in Dhaka.
function bnDateFromInput(iso) {
    return iso ? getBnDate(new Date(`${iso}T12:00:00`)) : getBnDate();
}
// YYYY-MM-DD ⇆ Date (local parts) for the DatePickerField, which works with Date
// objects. Local parts are used so the picked calendar day is preserved exactly.
function isoToDate(iso) {
    return iso ? new Date(`${iso}T12:00:00`) : null;
}
function dateToISO(d) {
    if (!d) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Clock time in Bangladesh (UTC+6), whatever timezone the browser is in.
function getBnTime(d = new Date()) {
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    return dt.toLocaleTimeString('bn-BD', { timeZone: BD_TZ, hour: '2-digit', minute: '2-digit', hour12: true });
}

// Map a saved Sohoz Job customer → invoice customer shape.
const custName = (c) => c?.applicant_name_bn || c?.applicant_name_en || '';
// Best-effort readable address from the saved customer's free-text present-address
// parts (district/upazila are stored as geo IDs, so they're intentionally skipped).
const custAddr = (c) => [c?.present_village_road_house, c?.present_post_office].filter(Boolean).join(', ');
const mapCustomer = (c) => ({ id: c?.id ?? null, name: custName(c), phone: c?.mobile_number || '', address: custAddr(c) });

// ── Shared input class ────────────────────────────────────────────────────────
const inputCls =
    'w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40';

// Print/save an HTML string via a throwaway hidden iframe.
function printHtml(html) {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => { document.body.removeChild(iframe); URL.revokeObjectURL(url); }, 1000);
    };
}

// Load an image URL with onload/onerror + a hard timeout (never a bare decode(),
// which can hang forever on large font-embedded SVGs). crossOrigin keeps the canvas
// untainted so toDataURL()/toBlob() work.
function loadImageRobust(url, timeoutMs = 20000) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(() => reject(new Error('রেন্ডার টাইমআউট')), timeoutMs);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('রেন্ডার ব্যর্থ')); };
        img.crossOrigin = 'anonymous';
        img.src = url;
    });
}

// Rasterize an invoice HTML document to a canvas. WHY html-to-image (toSvg) instead
// of html2canvas: html2canvas re-implements CSS and mis-renders several designs
// (writing-mode text, some flex/badge layouts) so exports drifted from the preview.
// toSvg serialises the node into an SVG <foreignObject> and lets the browser's OWN
// engine paint it — so the PDF/copy/print are pixel-identical to what the user sees.
// The invoice is written into a hidden iframe (its own document + fonts), sized to
// the chosen paper width; height grows to fit the content (min = one whole page).
async function invoiceHtmlToCanvas(html, paper) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = `position:fixed;left:-10000px;top:0;border:0;background:#fff;width:${paper.w}px;`;
    document.body.appendChild(iframe);
    try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open(); doc.write(html); doc.close();
        // Pre-build the embedded-font CSS (data URLs) in parallel with layout settling.
        // Passing it to toSvg as `fontEmbedCSS` is what makes the design fonts actually
        // render in the SVG — html-to-image's own font detection is broken for iframe
        // content (see getInvoiceFontEmbedCSS), so without this the export falls back to
        // a wide system font and the invoice number / date / totals wrap.
        const fontEmbedCSSPromise = getInvoiceFontEmbedCSS();
        try { await doc.fonts?.ready; } catch { /* fonts optional */ }
        await new Promise((r) => setTimeout(r, 450)); // let images/layout settle
        let h = Math.max(paper.h || 0, doc.body.scrollHeight || 0);
        // Round up to whole pages for fixed-size paper so the footer (margin-top:auto)
        // lands at the bottom of the LAST page and multi-page tiling stays clean.
        // 24px tolerance: the full-page stretch can render a few px past the sheet,
        // and a 2px slack turned that sliver into a whole blank trailing page.
        // (Paginated docs are exact page multiples, so the tolerance never clips them.)
        if (paper.h && paper.h > 0) h = Math.max(paper.h, Math.ceil((h - 24) / paper.h) * paper.h);
        iframe.style.height = `${h}px`;
        const { toSvg } = await import('html-to-image');
        const fontEmbedCSS = await fontEmbedCSSPromise;
        const svgUrl = await toSvg(doc.body, {
            backgroundColor: '#ffffff', cacheBust: true, width: paper.w, height: h,
            ...(fontEmbedCSS ? { fontEmbedCSS } : {}),
        });
        const img = await loadImageRobust(svgUrl);
        const RATIO = 2; // ~2× DPI for crisp text/print
        const canvas = document.createElement('canvas');
        canvas.width = paper.w * RATIO;
        canvas.height = Math.max(1, h) * RATIO;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas;
    } finally {
        document.body.removeChild(iframe);
    }
}

// Download an invoice as a PDF at the chosen paper size (tiles tall content across
// fixed-size pages; the slip uses a single content-height page).
async function invoiceToPdf(html, paper, filename) {
    const canvas = await invoiceHtmlToCanvas(html, paper);
    const { jsPDF } = await import('jspdf');
    const imgData = canvas.toDataURL('image/png');
    const pageW = paper.w;
    const imgH = canvas.height * pageW / canvas.width;
    const fixedH = !!(paper.h && paper.h > 0);
    const pageH = fixedH ? paper.h : imgH;
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [pageW, pageH], compress: true });
    if (!fixedH) {
        pdf.addImage(imgData, 'PNG', 0, 0, pageW, imgH);
    } else {
        let remaining = imgH;
        let position = 0;
        pdf.addImage(imgData, 'PNG', 0, position, pageW, imgH);
        remaining -= pageH;
        while (remaining > 1) {
            position -= pageH;
            pdf.addPage([pageW, pageH], 'portrait');
            pdf.addImage(imgData, 'PNG', 0, position, pageW, imgH);
            remaining -= pageH;
        }
    }
    pdf.save(`${filename || 'invoice'}.pdf`);
}

// Copy an invoice to the clipboard as a PNG image (paste into WhatsApp / mail / …).
async function invoiceToClipboard(html, paper) {
    const canvas = await invoiceHtmlToCanvas(html, paper);
    const blob = await new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('image failed'))), 'image/png'));
    await copyImageToClipboard(blob);
}

// Print an invoice by rasterizing it (same renderer as the PDF/preview, so the print
// matches exactly across every template) and opening the browser print dialog with a
// paginated image — no preview modal.
async function printInvoiceRaster(html, paper) {
    const canvas = await invoiceHtmlToCanvas(html, paper);
    const imgData = canvas.toDataURL('image/png');
    const fixedH = !!(paper.h && paper.h > 0);
    // Size the print page in PHYSICAL mm (96px = 1in = 25.4mm), not px. A px `@page` size
    // isn't reliably honoured — Chrome falls back to the selected physical paper, leaving
    // the fixed-px image narrower than the sheet (it sits left with a big right gap). mm
    // makes the page exactly the paper size and the image fills its full width.
    const wMm = +(paper.w / 96 * 25.4).toFixed(2);
    const hMm = fixedH ? +(paper.h / 96 * 25.4).toFixed(2) : 0;
    const imgHcss = canvas.height * paper.w / canvas.width; // CSS px at paper width
    let pages;
    if (fixedH) {
        const count = Math.max(1, Math.round(imgHcss / paper.h));
        pages = Array.from({ length: count }, (_, i) =>
            `<div class="pg"><img src="${imgData}" style="position:absolute;left:0;top:${-i * hMm}mm;width:${wMm}mm;display:block"></div>`
        ).join('');
    } else {
        pages = `<div class="pg" style="height:auto"><img src="${imgData}" style="width:${wMm}mm;display:block"></div>`;
    }
    const pageRule = fixedH ? `@page{size:${wMm}mm ${hMm}mm;margin:0}` : '@page{size:76mm auto;margin:0}';
    const doc = `<!doctype html><html><head><meta charset="utf-8"><style>
*{margin:0;padding:0;box-sizing:border-box}html,body{background:#fff}
${pageRule}
.pg{position:relative;width:${wMm}mm;height:${fixedH ? hMm + 'mm' : 'auto'};overflow:hidden;background:#fff;break-after:page;page-break-after:always}
.pg:last-child{break-after:auto;page-break-after:auto}
@media print{html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>${pages}</body></html>`;
    return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('aria-hidden', 'true');
        iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0';
        iframe.srcdoc = doc;
        iframe.onload = () => {
            setTimeout(() => {
                try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
                setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* ignore */ } resolve(); }, 800);
            }, 250);
        };
        document.body.appendChild(iframe);
    });
}

export default function InvoicePage({ presetCustomer = null, onPresetConsumed, presetEditInvoice = null, onEditPresetConsumed, presetDraft = null, onDraftConsumed, onLedgerChanged }) {
    const { t } = useTranslation();
    const { user } = useAuthStore();
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);
    // Invoice header = the ACTIVE shop. Name, address, phone and logo come from the
    // selected office's business profile; phone falls back to the owner's personal
    // number. Email is the SHOP's own contact email — no personal-email fallback, so
    // when the shop hasn't set one the invoice simply omits the email line.
    const shopInfo = {
        shopName: selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName,
        businessAddress: selectedOfficeSpace?.businessProfile?.businessAddress || user?.businessAddress,
        phone: selectedOfficeSpace?.businessProfile?.shopPhone || user?.phone,
        email: selectedOfficeSpace?.businessProfile?.shopEmail || '',
        shopLogo: selectedOfficeSpace?.businessProfile?.shopLogo || '',
        // Facebook page (business profile) → shown in the invoice header contact
        // block, gated by the global `showFacebook` invoice setting.
        facebookUrl: selectedOfficeSpace?.businessProfile?.facebookUrl || '',
        // The current signed-in user's name — rendered under the shop name on every
        // template with a "প্রোপ্রাইটরঃ" (proprietor) label.
        proprietorName: user?.name || '',
    };
    // Invoice-number prefix = the active shop's initials (see shopPrefix).
    const invoicePrefix = useMemo(() => shopPrefix(shopInfo.shopName), [shopInfo.shopName]);
    // Invoice design chosen in Settings → আয় ব্যয় হিসেব (falls back to the first).
    const selectedTemplate = user?.settings?.invoiceTemplate || DEFAULT_INVOICE_TEMPLATE;
    // Appearance settings (font scale + watermark) from the invoice settings popup.
    const invoiceSettings = user?.settings?.invoiceSettings;

    // ── View: saved list vs. create/edit form ─────────────────────────────────
    const [view, setView] = useState('list'); // 'list' | 'form'
    const [invoices, setInvoices] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // ── Form state ────────────────────────────────────────────────────────────
    const [editingId, setEditingId] = useState(null);
    const [customerId, setCustomerId] = useState(null); // linked Sohoz Job customer id
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerAddress, setCustomerAddress] = useState('');
    const [items, setItems] = useState([{ id: 1, name: '', qty: 1, price: '' }]);
    const [discount, setDiscount] = useState('');
    const [deposit, setDeposit] = useState('');
    // While false, জমা (deposit) auto-tracks the running সর্বমোট so adding services keeps
    // the invoice paid-in-full; the moment the user types a জমা themselves it flips true
    // and we never overwrite their (partial) amount again. Reset per form/flow below.
    const depositTouchedRef = useRef(false);
    // Ids of service rows already represented in the আয়/ব্যয় ledger — either pushed via
    // the "add to ledger" button, or auto-loaded FROM the customer's same-day ledger.
    // The button stays disabled until at least one NEW (un-pushed) service line exists,
    // and each push only sends the rows not already committed (never double-counts).
    const [committedItemIds, setCommittedItemIds] = useState(() => new Set());
    const [ledgerBusy, setLedgerBusy] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState(() => genInvoiceNo());
    // The invoice date is user-pickable (defaults to today). Stored as YYYY-MM-DD for
    // the date input; the Bengali display shown on the invoice is derived from it.
    const [invoiceDateISO, setInvoiceDateISO] = useState(() => toDateInput());
    const invoiceDate = useMemo(() => bnDateFromInput(invoiceDateISO), [invoiceDateISO]);
    const [saving, setSaving] = useState(false);
    const [busyExport, setBusyExport] = useState(null); // 'pdf' | 'copy' | 'print' | null

    // ── Customer picker ───────────────────────────────────────────────────────
    const [customers, setCustomers] = useState([]);
    const [custOpen, setCustOpen] = useState(false);

    // ── Customer QR (name + phone) — regenerated as the customer fields change ──
    const [qrDataUrl, setQrDataUrl] = useState('');
    useEffect(() => {
        let cancelled = false;
        buildQrDataUrl(customerName, customerPhone).then((url) => { if (!cancelled) setQrDataUrl(url); });
        return () => { cancelled = true; };
    }, [customerName, customerPhone]);

    // ── Load saved invoices ───────────────────────────────────────────────────
    const loadInvoices = useCallback(async () => {
        setListLoading(true);
        try {
            const res = await invoiceService.list({ limit: 100 });
            setInvoices(res.data ?? []);
        } catch (err) {
            notifications.show({ message: err.message || 'লোড করা যায়নি', color: 'red' });
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => { loadInvoices(); }, [loadInvoices]);

    // ── Load customers once (for the picker) ──────────────────────────────────
    useEffect(() => {
        sohozJobCustomerService.list()
            .then((res) => setCustomers(res.data || []))
            .catch(() => {});
    }, []);

    // ── Reset the form for a brand-new invoice ────────────────────────────────
    const resetForm = useCallback((customer = null) => {
        const c = customer ? mapCustomer(customer) : { id: null, name: '', phone: '', address: '' };
        setEditingId(null);
        setCustomerId(c.id);
        setCustomerName(c.name);
        setCustomerPhone(c.phone);
        setCustomerAddress(c.address || '');
        setItems([{ id: Date.now(), name: '', qty: 1, price: '' }]);
        setDiscount('');
        setDeposit('');
        depositTouchedRef.current = false; // fresh invoice → জমা auto-tracks the total
        setCommittedItemIds(new Set());
        setInvoiceNumber(genInvoiceNo(invoicePrefix));
        setInvoiceDateISO(toDateInput());
    }, [invoicePrefix]);

    // Keep a NEW invoice's prefix in sync with the active shop: when the shop name
    // resolves (or changes) the numeric suffix is preserved, only the prefix swaps.
    // Editing an existing invoice keeps its saved number untouched.
    useEffect(() => {
        if (editingId) return;
        setInvoiceNumber((prev) => {
            const digits = (String(prev).match(/(\d{3,})$/) || [])[1] || String(Math.floor(1000 + Math.random() * 9000));
            return `${invoicePrefix}-${digits}`;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoicePrefix]);

    // Pull every আয়/ব্যয় entry logged for this customer TODAY and turn each into an
    // invoice line (name → বিবরণ, amount → দাম, qty 1). জমা becomes the sum of the নগদ
    // (cash, isDue=false) entries, so the auto status marks পরিশোধিত/বকেয়া correctly.
    // The client-side customerId filter keeps this safe before the backend ships the
    // column/filter (entries without a customerId simply never match). No-ops when
    // nothing matches, so callers that want a reset must clear the lines first.
    const fillFromCustomerTransactions = useCallback(async (cid) => {
        if (!cid) return;
        const today = toDateInput();
        try {
            const res = await incomeExpenseService.list({ customerId: cid, dateFrom: today, dateTo: today, limit: 200 });
            const list = (res.data ?? []).filter((e) => e.customerId != null && String(e.customerId) === String(cid));
            if (!list.length) return;
            const rows = list.map((e, i) => ({ id: Date.now() + i, name: e.name || '', qty: 1, price: e.amount ?? '' }));
            const paid = list.reduce((s, e) => s + (e.isDue ? 0 : (Number(e.amount) || 0)), 0);
            setItems(rows);
            setDiscount('');
            setDeposit(paid ? String(paid) : '');
            // The customer's actual cash paid is a specific amount — preserve it (a newly
            // added service shouldn't silently auto-mark itself paid).
            depositTouchedRef.current = true;
            // These rows ARE existing ledger entries → mark committed so the button stays
            // disabled until the user adds a brand-new service line.
            setCommittedItemIds(new Set(rows.map((r) => r.id)));
        } catch {
            /* backend customerId filter not available yet — leave the form untouched */
        }
    }, []);

    const openCreate = useCallback((customer = null) => {
        resetForm(customer);
        setView('form');
    }, [resetForm]);

    // Open a fresh invoice pre-filled from a ledger draft (income modal → invoice).
    // Seeds the just-saved line first; when a customer is linked we then replace it with
    // that customer's FULL same-day history (which already includes the entry just saved).
    // The single line is the fallback if the fetch fails. A cash (নগদ) entry arrives as
    // paid → deposit = total → auto পরিশোধিত; a due (বাকি) entry keeps deposit empty → বকেয়া.
    const openCreateWithDraft = useCallback((draft) => {
        resetForm(null);
        const c = draft?.customer;
        if (c) {
            setCustomerId(c.id ?? null);
            setCustomerName(c.name || '');
            setCustomerPhone(c.phone || '');
            setCustomerAddress(c.address || '');
        }
        const rows = Array.isArray(draft?.items) && draft.items.length
            ? draft.items.map((it, i) => ({ id: Date.now() + i, name: it.name || '', qty: it.qty ?? 1, price: it.price ?? '' }))
            : [{ id: Date.now(), name: '', qty: 1, price: '' }];
        setItems(rows);
        if (draft?.status === 'paid') {
            const total = rows.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
            setDeposit(String(total));
            depositTouchedRef.current = false; // paid in full → keep জমা tracking the total
        } else {
            depositTouchedRef.current = true;  // বাকি draft → keep জমা empty, don't auto-fill
        }
        // The just-saved line is already in the ledger (fromLedger); mark it committed.
        // If a customer is linked, fillFromCustomerTransactions below overrides items and
        // re-marks the full same-day list committed.
        if (draft?.fromLedger) setCommittedItemIds(new Set(rows.map((r) => r.id)));
        setView('form');
        // With a customer linked, show the whole day's history for them (includes the
        // entry just saved); the single line above stays as the fallback.
        if (c?.id) fillFromCustomerTransactions(c.id);
    }, [resetForm, fillFromCustomerTransactions]);

    const openEdit = (inv) => {
        setEditingId(inv.id);
        setCustomerId(inv.customerId ?? null);
        setCustomerName(inv.customerName || '');
        setCustomerPhone(inv.customerPhone || '');
        setCustomerAddress(inv.customerAddress || '');
        const its = Array.isArray(inv.items) && inv.items.length
            ? inv.items.map((it, i) => ({ id: i + 1, name: it.name || '', qty: it.qty ?? 1, price: it.price ?? '' }))
            : [{ id: 1, name: '', qty: 1, price: '' }];
        setItems(its);
        setDiscount(inv.discount ? String(inv.discount) : '');
        const savedDeposit = invoiceDeposit(inv);
        setDeposit(savedDeposit ? String(savedDeposit) : '');
        depositTouchedRef.current = true; // editing a saved invoice → keep its জমা exactly
        setCommittedItemIds(new Set());
        setInvoiceNumber(inv.invoiceNumber);
        setInvoiceDateISO(toDateInput(inv.invoiceDate || inv.createdAt));
        setView('form');
    };

    // ── Consume a customer preselected from the Customers tab ──────────────────
    useEffect(() => {
        if (presetCustomer) {
            openCreate(presetCustomer);
            // Same-day আয়/ব্যয় for this customer auto-fill the service lines.
            fillFromCustomerTransactions(presetCustomer.id ?? null);
            onPresetConsumed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetCustomer]);

    // ── Consume an invoice to edit, sent from the customer-details page ────────
    useEffect(() => {
        if (presetEditInvoice) {
            openEdit(presetEditInvoice);
            onEditPresetConsumed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetEditInvoice]);

    // ── Consume a ledger draft (income modal's "Save & Invoice") ───────────────
    useEffect(() => {
        if (presetDraft) {
            openCreateWithDraft(presetDraft);
            onDraftConsumed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetDraft]);

    // ── Computed totals ───────────────────────────────────────────────────────
    const subtotal = useMemo(
        () => items.reduce((sum, it) => sum + qtyOf(it.qty) * parseNum(it.price), 0),
        [items]
    );
    const grandTotal = Math.max(0, subtotal - parseNum(discount));

    // Auto-fill জমা to the running সর্বমোট while the user hasn't set it themselves — so
    // adding/removing services (incl. bulk-add) keeps the deposit equal to the total
    // (paid-in-full) instead of a stale number. Once the user types a জমা, depositTouchedRef
    // is true and this no-ops, preserving their partial amount.
    useEffect(() => {
        if (depositTouchedRef.current) return;
        const next = grandTotal > 0 ? String(grandTotal) : '';
        setDeposit((prev) => (prev === next ? prev : next));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grandTotal]);

    const due = grandTotal - parseNum(deposit);
    // Status follows the deposit (জমা), so it can never disagree with the money: জমা ≥
    // total is পরিশোধিত, anything less is বকেয়া. The two buttons set জমা accordingly —
    // পরিশোধিত fills it with the total, বকেয়া clears a full জমা. A partial জমা is kept
    // and blocks পরিশোধিত, because money is still owed.
    const hasAmount = grandTotal > 0;
    const canMarkPaid = hasAmount && parseNum(deposit) >= grandTotal;
    const canPickPaid = hasAmount && (canMarkPaid || parseNum(deposit) === 0);
    const effectiveStatus = canMarkPaid ? 'paid' : 'due';
    const isPaid = effectiveStatus === 'paid';
    const pickPaid = () => {
        if (!canPickPaid || canMarkPaid) return;
        depositTouchedRef.current = false; // paid in full → keep জমা tracking the total
        setDeposit(String(grandTotal));
    };
    const pickDue = () => {
        if (!canMarkPaid) return;
        depositTouchedRef.current = true; // stay বকেয়া as services are added
        setDeposit('');
    };

    // The customer's unpaid dues on their other invoices, when the setting is enabled.
    const computePrevDue = useCallback((cid, cphone, excludeId) => {
        if (!invoiceSettings?.showPreviousDue) return 0;
        return customerPreviousDue(invoices, cid, cphone, excludeId);
    }, [invoices, invoiceSettings?.showPreviousDue]);
    const previousDue = useMemo(
        () => computePrevDue(customerId, customerPhone, editingId),
        [computePrevDue, customerId, customerPhone, editingId]
    );

    // Push each NOT-yet-pushed service line straight into the আয়/ব্যয় ledger as its own
    // income entry (name + amount = qty×দাম), linked to the invoice's customer. No modal.
    // নগদ vs বাকি follows the invoice status. Only un-committed rows are sent, so pressing
    // it again (after adding a new service) never re-adds the earlier lines.
    const handleAddToLedger = async () => {
        if (ledgerBusy) return;
        const toAdd = items.filter(it => (it.name.trim() || parseNum(it.price) > 0) && !committedItemIds.has(it.id));
        if (!toAdd.length) return;
        setLedgerBusy(true);
        try {
            await Promise.all(toAdd.map(it => incomeExpenseService.create({
                name: it.name.trim() || 'সেবা',
                type: 'income',
                amount: qtyOf(it.qty) * parseNum(it.price),
                category: 'manual',
                isDue: effectiveStatus === 'due',
                ...(customerId ? { customerId } : {}),
            })));
            setCommittedItemIds(prev => new Set([...prev, ...toAdd.map(it => it.id)]));
            notifications.show({ message: 'আয় ব্যয়ে যোগ হয়েছে', color: 'green' });
            onLedgerChanged?.();
        } catch (err) {
            notifications.show({ message: err.message || 'হিসাবে যোগ করা যায়নি', color: 'red' });
        } finally {
            setLedgerBusy(false);
        }
    };

    // ── Item helpers ──────────────────────────────────────────────────────────
    const addItem = () => setItems(prev => [...prev, { id: Date.now(), name: '', qty: 1, price: '' }]);
    const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));
    const updateItem = (id, field, value) =>
        setItems(prev => prev.map(it => (it.id === id ? { ...it, [field]: value } : it)));

    // Every service the shop offers → suggested in the service-name field: the AUTO
    // modules (with their prices) first, then the shop's own profile services (with
    // any saved price). Priced services show the amount and auto-fill দাম on pick.
    // The user may still type any custom service (free text).
    const serviceOptions = useMemo(() => {
        return buildShopServiceList(user?.settings, selectedOfficeSpace?.businessProfile).map(s => {
            const hasPrice = s.price != null && s.price !== '';
            return {
                label: s.name,
                value: s.name,
                price: hasPrice ? Number(s.price) : null,
                hint: hasPrice ? `${fmtAmount(s.price)} ৳` : undefined,
            };
        });
    }, [user?.settings, selectedOfficeSpace?.businessProfile]);

    // ── Bulk add: the top সেবা যোগ opens a multi-select of the shop's services;
    // Apply appends all chosen ones (with their prices) as item rows at once. ──
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkSel, setBulkSel] = useState([]);
    const toggleBulk = (val) =>
        setBulkSel((s) => (s.includes(val) ? s.filter((v) => v !== val) : [...s, val]));
    const applyBulk = () => {
        const chosen = serviceOptions.filter((o) => bulkSel.includes(o.value));
        if (chosen.length) {
            setItems((prev) => {
                const kept = prev.filter((it) => it.name.trim() || String(it.price).trim());
                const rows = chosen.map((o, i) => ({ id: Date.now() + i, name: o.value, qty: 1, price: o.price != null ? String(o.price) : '' }));
                return [...kept, ...rows];
            });
        }
        setBulkSel([]);
        setBulkOpen(false);
    };

    // ── Customer picker helpers ───────────────────────────────────────────────
    const custSuggestions = useMemo(() => {
        const q = customerName.trim().toLowerCase();
        const base = q
            ? customers.filter(c =>
                custName(c).toLowerCase().includes(q) ||
                (c.applicant_name_en || '').toLowerCase().includes(q) ||
                (c.mobile_number || '').includes(q))
            : customers;
        return base.slice(0, 8);
    }, [customers, customerName]);

    const pickCustomer = (c) => {
        const m = mapCustomer(c);
        setCustomerId(m.id);
        setCustomerName(m.name);
        setCustomerPhone(m.phone);
        setCustomerAddress(m.address || '');
        setCustOpen(false);
        // Editing a saved invoice must not be refilled — only auto-fill for new ones.
        if (!editingId) {
            // Switching the customer resets the bill first, then loads the NEW customer's
            // same-day entries (leaves a blank line if they have none logged today).
            setItems([{ id: Date.now(), name: '', qty: 1, price: '' }]);
            setDiscount('');
            setDeposit('');
            depositTouchedRef.current = false; // fresh bill → auto-track (fill overrides if data)
            setCommittedItemIds(new Set());
            fillFromCustomerTransactions(m.id);
        }
    };

    // ── Save (create or update) ───────────────────────────────────────────────
    const filledItems = items.filter(it => it.name.trim() || parseNum(it.price) > 0);
    const isEmpty = filledItems.length === 0;
    // Are there filled service lines not yet pushed to the ledger? Drives the
    // "add to ledger" button's enabled state.
    const hasUnpushedLedgerItems = filledItems.some(it => !committedItemIds.has(it.id));

    const handleSave = async () => {
        if (isEmpty) {
            notifications.show({ message: 'অন্তত একটি সেবা যোগ করুন', color: 'red' });
            return;
        }
        setSaving(true);
        const payload = {
            invoiceNumber,
            customerId: customerId ?? null,
            customerName: customerName.trim() || null,
            customerPhone: customerPhone.trim() || null,
            customerAddress: customerAddress.trim() || null,
            items: filledItems.map(it => ({ name: it.name.trim(), qty: parseNum(it.qty), price: parseNum(it.price) })),
            discount: parseNum(discount),
            deposit: parseNum(deposit),
            status: effectiveStatus,
            invoiceDate: invoiceDateISO,
        };
        try {
            if (editingId) {
                await invoiceService.update(editingId, payload);
                notifications.show({ message: 'ইনভয়েস আপডেট হয়েছে', color: 'green' });
            } else {
                await invoiceService.create(payload);
                notifications.show({ message: 'ইনভয়েস সেভ হয়েছে', color: 'green' });
            }
            await loadInvoices();
            setView('list');
        } catch (err) {
            notifications.show({ message: err.message || 'সেভ করা যায়নি', color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDeleteId) return;
        setDeleting(true);
        try {
            await invoiceService.remove(confirmDeleteId);
            notifications.show({ message: 'ইনভয়েস মুছে ফেলা হয়েছে', color: 'green' });
            setConfirmDeleteId(null);
            await loadInvoices();
        } catch (err) {
            notifications.show({ message: err.message || 'মুছে ফেলা যায়নি', color: 'red' });
        } finally {
            setDeleting(false);
        }
    };

    // ── Print / Export ──────────────────────────────────────────────────────────
    const invoiceData = () => ({
        shop: shopInfo,
        customerName, customerPhone, customerAddress, invoiceNumber, invoiceDate,
        items: filledItems.map(it => ({ name: it.name, qty: it.qty, price: it.price })),
        discount, deposit, isPaid, qr: qrDataUrl, previousDue,
        footerServicesLabel: t('invoiceSettings.footerServicesLabel') || 'আমাদের সেবা সমূহ',
    });
    // For canvas exports (PDF / copy / print image): the shop logo lives on R2 which
    // sends no CORS header, so html-to-image's toSvg can't fetch it. Proxy it to a
    // data URL first. The signature is left AS-IS (an SVG data URL renders natively in
    // toSvg's foreignObject, identical to the preview — no rasterisation, which would
    // distort a no-intrinsic-size SVG and make the export differ from the preview).
    const proxyShopLogo = async () => {
        const logo = shopInfo.shopLogo;
        if (logo && /^https?:/i.test(logo)) {
            try {
                return { ...shopInfo, shopLogo: await questionPaperService.proxyImageDataUrl(logo) };
            } catch { /* fall back to the original URL (logo just won't render) */ }
        }
        return shopInfo;
    };
    const buildExportAssets = async () => ({ shop: await proxyShopLogo(), settings: invoiceSettings });
    // Exports use the PAGINATED document: a long invoice becomes explicit fixed-height
    // pages (header repeated, totals/footer on the last page, পৃষ্ঠা N/M markers) that
    // the raster tiling slices along exactly.
    const buildExportHTML = async () => {
        const { shop, settings } = await buildExportAssets();
        return buildPaginatedInvoiceHTML(selectedTemplate, { ...invoiceData(), shop }, settings, { mode: 'export' });
    };

    // Live preview (right column) — rebuilt as the form changes or template switches.
    // The instant single-document draft renders first; the async paginated build then
    // replaces it so overflowing invoices show as separate pages (identical string
    // when it fits one page, so no extra rewrite happens).
    const [livePreviewHtml, setLivePreviewHtml] = useState('');
    useEffect(() => {
        let cancelled = false;
        setLivePreviewHtml(buildInvoiceHTML(selectedTemplate, invoiceData(), invoiceSettings));
        const id = setTimeout(() => {
            buildPaginatedInvoiceHTML(selectedTemplate, invoiceData(), invoiceSettings, { mode: 'preview' })
                .then((html) => { if (!cancelled) setLivePreviewHtml(html); })
                .catch(() => { /* keep the single-page draft */ });
        }, 300);
        return () => { cancelled = true; clearTimeout(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTemplate, invoiceSettings, items, customerName, customerPhone, customerAddress, discount, deposit, invoiceNumber, invoiceDate, isPaid, qrDataUrl, previousDue, selectedOfficeSpace?.id]);

    // Download the invoice as a real PDF (no print dialog).
    const handleDownload = async () => {
        if (isEmpty || busyExport) return;
        setBusyExport('pdf');
        try {
            await invoiceToPdf(await buildExportHTML(), paperPx(invoiceSettings?.paperSize), invoiceNumber);
        } catch (err) {
            notifications.show({ message: err?.message || 'PDF তৈরি করা যায়নি', color: 'red' });
        } finally {
            setBusyExport(null);
        }
    };
    // Copy the whole invoice to the clipboard as an image.
    const handleCopy = async () => {
        if (isEmpty || busyExport) return;
        setBusyExport('copy');
        try {
            await invoiceToClipboard(await buildExportHTML(), paperPx(invoiceSettings?.paperSize));
            notifications.show({ message: 'ইনভয়েস কপি হয়েছে — এখন পেস্ট করুন', color: 'green' });
        } catch (err) {
            const msg = err?.message === 'clipboard-unsupported'
                ? 'এই ব্রাউজারে কপি সাপোর্ট করে না'
                : (err?.message || 'কপি করা যায়নি');
            notifications.show({ message: msg, color: 'red' });
        } finally {
            setBusyExport(null);
        }
    };
    // Print: rasterize the invoice (identical to the preview) and open the browser
    // print dialog directly — no preview modal, works the same for every template.
    const handlePrintDirect = async () => {
        if (isEmpty || busyExport) return;
        setBusyExport('print');
        try {
            await printInvoiceRaster(await buildExportHTML(), paperPx(invoiceSettings?.paperSize));
        } catch (err) {
            notifications.show({ message: err?.message || 'প্রিন্ট করা যায়নি', color: 'red' });
        } finally {
            setBusyExport(null);
        }
    };

    // Download a SAVED invoice straight from the list (no need to open it).
    const downloadInvoice = async (inv) => {
        const qr = await buildQrDataUrl(inv.customerName, inv.customerPhone);
        const { shop, settings } = await buildExportAssets();
        const html = await buildPaginatedInvoiceHTML(selectedTemplate, {
            shop,
            customerName: inv.customerName,
            customerPhone: inv.customerPhone,
            customerAddress: inv.customerAddress,
            previousDue: computePrevDue(inv.customerId ?? null, inv.customerPhone, inv.id),
            invoiceNumber: inv.invoiceNumber,
            invoiceDate: inv.invoiceDate ? bnDateFromInput(inv.invoiceDate) : getBnDate(inv.createdAt),
            items: Array.isArray(inv.items) ? inv.items : [],
            discount: inv.discount,
            deposit: invoiceDeposit(inv),
            isPaid: inv.status === 'paid',
            qr,
            footerServicesLabel: t('invoiceSettings.footerServicesLabel') || 'আমাদের সেবা সমূহ',
        }, settings, { mode: 'export' });
        try {
            await invoiceToPdf(html, paperPx(invoiceSettings?.paperSize), inv.invoiceNumber);
        } catch (err) {
            notifications.show({ message: err?.message || 'PDF তৈরি করা যায়নি', color: 'red' });
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // LIST VIEW — saved invoices
    // ═══════════════════════════════════════════════════════════════════════════
    if (view === 'list') {
        return (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 dark:border-zinc-800/60">
                    <div className="flex items-center gap-2">
                        <IconFileInvoice size={18} className="text-zinc-500 dark:text-zinc-400" />
                        <h3 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">সেভ করা ইনভয়েস</h3>
                        {!listLoading && <span className="text-xs text-zinc-400 dark:text-zinc-500">({toBn(invoices.length)})</span>}
                    </div>
                    <button
                        type="button"
                        onClick={() => openCreate()}
                        className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm shadow-primary/20"
                    >
                        <IconPlus size={15} />
                        নতুন ইনভয়েস
                    </button>
                </div>

                {listLoading ? (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] border-collapse text-sm">
                            <thead>
                                <tr className="text-left">
                                    {['ইনভয়েস নং', 'গ্রাহক', 'তারিখ', 'সর্বমোট', 'অবস্থা', ''].map((h, i) => (
                                        <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${i === 3 ? 'text-right' : ''}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: 5 }).map((_, r) => (
                                    <tr key={r} className="border-b border-zinc-100 dark:border-zinc-800/40">
                                        <td className="py-[15px] px-4 first:pl-5"><div className="h-4 w-16 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                        <td className="py-[15px] px-4"><div className="h-4 w-28 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /><div className="mt-1.5 h-3 w-20 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                        <td className="py-[15px] px-4"><div className="h-4 w-24 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                        <td className="py-[15px] px-4"><div className="ml-auto h-4 w-12 rounded bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                        <td className="py-[15px] px-4"><div className="h-6 w-20 rounded-full bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></td>
                                        <td className="py-[15px] px-4 last:pr-5"><div className="ml-auto flex w-fit items-center gap-1.5"><div className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" /><div className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" /><div className="h-8 w-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 animate-pulse" /></div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : invoices.length === 0 ? (
                    <div className="py-16 flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                            <IconFileInvoice size={28} className="text-zinc-400 dark:text-zinc-600" />
                        </div>
                        <p className="text-sm text-zinc-500">এখনও কোনো ইনভয়েস সেভ করা হয়নি</p>
                        <button
                            type="button"
                            onClick={() => openCreate()}
                            className="mt-1 flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={15} />
                            নতুন ইনভয়েস তৈরি করুন
                        </button>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] border-collapse text-sm">
                            <thead>
                                <tr className="text-left">
                                    {['ইনভয়েস নং', 'গ্রাহক', 'তারিখ', 'সর্বমোট', 'অবস্থা', ''].map((h, i) => (
                                        <th key={i} className={`py-3 px-4 first:pl-5 last:pr-5 text-[12px] font-semibold text-zinc-500 dark:text-zinc-400 border-b border-zinc-100 dark:border-zinc-800/60 ${i === 3 ? 'text-right' : ''}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id} className="border-b border-zinc-100 dark:border-zinc-800/40 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-colors group">
                                        <td className="py-[13px] px-4 first:pl-5 font-semibold text-zinc-800 dark:text-zinc-100 tabular-nums whitespace-nowrap">{inv.invoiceNumber}</td>
                                        <td className="py-[13px] px-4 text-zinc-700 dark:text-zinc-300">
                                            <span className="font-medium">{inv.customerName || '—'}</span>
                                            {inv.customerPhone && <span className="block text-[11px] text-zinc-400 dark:text-zinc-500">{inv.customerPhone}</span>}
                                            {inv.note && (
                                                <span className="mt-1 block max-w-[240px] whitespace-normal break-words text-[11.5px] leading-snug text-zinc-500 dark:text-zinc-400">
                                                    {inv.note}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-[13px] px-4 text-[13px] text-zinc-500 dark:text-zinc-400 whitespace-nowrap">
                                            {getBnDate(inv.createdAt)}
                                            <span className="ml-1.5 text-[11.5px] text-zinc-400 dark:text-zinc-500">{getBnTime(inv.createdAt)}</span>
                                        </td>
                                        <td className="py-[13px] px-4 text-right font-bold text-zinc-800 dark:text-zinc-100 tabular-nums whitespace-nowrap">{fmtAmount(inv.grandTotal)} ৳</td>
                                        <td className="py-[13px] px-4">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${
                                                inv.status === 'paid'
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                    : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${inv.status === 'paid' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                                {inv.status === 'paid' ? 'পরিশোধিত' : 'বকেয়া'}
                                            </span>
                                        </td>
                                        <td className="py-[13px] px-4 last:pr-5 text-right whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadInvoice(inv)}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30 transition-colors"
                                                        >
                                                            <IconDownload size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>ডাউনলোড</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => openEdit(inv)}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
                                                        >
                                                            <IconPencil size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>এডিট</TooltipContent>
                                                </Tooltip>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfirmDeleteId(inv.id)}
                                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                                        >
                                                            <IconTrash size={14} />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>ডিলিট</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Delete confirm */}
                <Dialog open={confirmDeleteId !== null} onOpenChange={(o) => { if (!o) setConfirmDeleteId(null); }}>
                    <DialogContent className="max-w-sm p-0 overflow-hidden">
                        <DialogTitle className="sr-only">ইনভয়েস ডিলিট</DialogTitle>
                        <div className="p-6 flex flex-col items-center text-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-red-500/10 dark:bg-red-900/20 flex items-center justify-center">
                                <IconAlertTriangle size={26} className="text-red-500" />
                            </div>
                            <div>
                                <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">ইনভয়েস মুছে ফেলবেন?</p>
                                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">এই কাজটি ফেরানো যাবে না।</p>
                            </div>
                        </div>
                        <div className="flex border-t border-zinc-100 dark:border-zinc-800">
                            <button type="button" onClick={() => setConfirmDeleteId(null)} className="flex-1 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-r border-zinc-100 dark:border-zinc-800">বাতিল</button>
                            <button type="button" onClick={handleDelete} disabled={deleting} className="flex-1 py-3 text-sm font-semibold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40">{deleting ? '...' : 'ডিলিট'}</button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // FORM VIEW — create / edit
    // ═══════════════════════════════════════════════════════════════════════════
    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <button
                        type="button"
                        onClick={() => setView('list')}
                        title="ফিরে যান"
                        className="mt-0.5 w-9 h-9 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex-shrink-0"
                    >
                        <IconArrowLeft size={17} />
                    </button>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-zinc-800 dark:text-zinc-100">{editingId ? 'ইনভয়েস এডিট' : 'ইনভয়েস তৈরি'}</h1>
                        <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">সেবা যোগ করে গ্রাহকের জন্য রসিদ বানান।</p>
                    </div>
                </div>
                {/* Mobile: 3-col grid so the six actions wrap onto two lines with full
                    labels instead of squishing into one cramped row. Desktop keeps a flex row. */}
                <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 sm:shrink-0 [&>*]:w-full sm:[&>*]:w-auto">
                    <InvoiceSettings shop={shopInfo} />
                    <button type="button" onClick={handleDownload} disabled={isEmpty || !!busyExport} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {busyExport === 'pdf'
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <IconDownload size={16} />}
                        PDF ডাউনলোড
                    </button>
                    <button type="button" onClick={handleCopy} disabled={isEmpty || !!busyExport} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-sky-600 hover:bg-sky-700 text-white text-xs sm:text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {busyExport === 'copy'
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <IconCopy size={16} />}
                        কপি
                    </button>
                    <button type="button" onClick={handlePrintDirect} disabled={isEmpty || !!busyExport} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {busyExport === 'print'
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <IconPrinter size={16} />}
                        প্রিন্ট
                    </button>
                    <button type="button" onClick={handleAddToLedger} disabled={isEmpty || ledgerBusy || !hasUnpushedLedgerItems} title={!isEmpty && !hasUnpushedLedgerItems ? 'সব সেবা ইতিমধ্যে হিসাবে যোগ হয়েছে' : undefined} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs sm:text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {ledgerBusy
                            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            : <IconWallet size={16} />}
                        আয় ব্যয়ে যোগ
                    </button>
                    <button type="button" onClick={handleSave} disabled={saving || isEmpty} className="flex items-center justify-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        <IconDeviceFloppy size={16} />
                        {saving ? 'সেভ হচ্ছে…' : (editingId ? 'আপডেট করুন' : 'সেভ করুন')}
                    </button>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

                {/* Left: Form */}
                <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-5 space-y-5">

                    {/* Customer info */}
                    <div>
                        <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100 mb-3">গ্রাহকের তথ্য</h2>
                        <div className="grid grid-cols-2 gap-3">
                            {/* Name + saved-customer picker */}
                            <div className="relative">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="গ্রাহকের নাম"
                                        value={customerName}
                                        onChange={e => { setCustomerName(sanitizeText(e.target.value)); setCustomerId(null); setCustOpen(true); }}
                                        onFocus={() => setCustOpen(true)}
                                        onClick={() => setCustOpen(true)}
                                        onBlur={() => setTimeout(() => setCustOpen(false), 150)}
                                        className={inputCls + ' pr-8'}
                                    />
                                    {customerId != null
                                        ? <button
                                            type="button"
                                            tabIndex={-1}
                                            title="গ্রাহক সরান"
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setCustomerId(null); setCustomerName(''); setCustomerPhone(''); setCustomerAddress('');
                                                // Also clear the services that were auto-loaded for this customer.
                                                // Skip when editing a saved invoice so its lines aren't wiped.
                                                if (!editingId) { setItems([{ id: Date.now(), name: '', qty: 1, price: '' }]); setDiscount(''); setDeposit(''); depositTouchedRef.current = false; }
                                                setCustOpen(false);
                                            }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-red-500"
                                          >
                                            <IconX size={15} />
                                          </button>
                                        : <IconUserSearch size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />}
                                </div>
                                {custOpen && custSuggestions.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg">
                                        {custSuggestions.map((c) => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); pickCustomer(c); }}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors"
                                            >
                                                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-bold">
                                                    {custName(c).charAt(0) || '?'}
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block text-sm text-zinc-800 dark:text-zinc-100 truncate">{custName(c)}</span>
                                                    <span className="block text-[11px] text-zinc-400 dark:text-zinc-500">{c.mobile_number}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="মোবাইল"
                                value={customerPhone}
                                onChange={e => setCustomerPhone(sanitizeText(e.target.value))}
                                className={inputCls}
                            />
                        </div>
                        {/* Address + invoice date. Side by side on desktop; stacked on mobile
                            so the date field is wide enough (the 3fr column squeezed the date
                            text onto three cramped lines). The date uses our custom picker
                            (Popover + calendar), not the native browser input. */}
                        <div className="grid grid-cols-1 sm:grid-cols-[7fr_3fr] gap-3 mt-3">
                            <input
                                type="text"
                                placeholder="গ্রাহকের ঠিকানা (ঐচ্ছিক)"
                                value={customerAddress}
                                onChange={e => setCustomerAddress(sanitizeText(e.target.value))}
                                className={inputCls}
                            />
                            <DatePickerField
                                value={isoToDate(invoiceDateISO)}
                                onChange={(date) => { if (date) setInvoiceDateISO(dateToISO(date)); }}
                                placeholder="ইনভয়েসের তারিখ"
                                toYear={new Date().getFullYear() + 1}
                                triggerClassName="w-full whitespace-nowrap px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                    </div>

                    {/* Products */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h2 className="text-base font-bold text-zinc-800 dark:text-zinc-100">সেবা</h2>
                            {/* Top button: bulk multi-select from the shop's services */}
                            <Popover open={bulkOpen} onOpenChange={setBulkOpen}>
                                <PopoverTrigger asChild>
                                    <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-100 dark:border-orange-800/40 text-xs font-semibold rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors">
                                        <IconPlus size={13} />
                                        সেবা যোগ
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-72 p-0">
                                    <div className="max-h-64 overflow-y-auto p-1">
                                        {serviceOptions.length === 0 ? (
                                            <p className="px-3 py-5 text-center text-xs text-zinc-400 dark:text-zinc-500">কোনো সেবা যোগ করা নেই</p>
                                        ) : serviceOptions.map((o) => {
                                            const checked = bulkSel.includes(o.value);
                                            return (
                                                <button
                                                    key={o.value}
                                                    type="button"
                                                    onClick={() => toggleBulk(o.value)}
                                                    className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                                >
                                                    <span className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${checked ? 'bg-primary border-primary text-white' : 'border-zinc-300 dark:border-zinc-600'}`}>
                                                        {checked && <IconCheck size={11} />}
                                                    </span>
                                                    <span className="min-w-0 flex-1 truncate text-sm text-zinc-800 dark:text-zinc-100">{o.label}</span>
                                                    {o.hint && <span className="flex-none text-xs text-zinc-400 dark:text-zinc-500 tabular-nums">{o.hint}</span>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <div className="flex items-center justify-between gap-2 border-t border-zinc-100 dark:border-zinc-800 p-2">
                                        <span className="text-[11px] text-zinc-400 dark:text-zinc-500">{toBn(bulkSel.length)} টি নির্বাচিত</span>
                                        <button
                                            type="button"
                                            onClick={applyBulk}
                                            disabled={!bulkSel.length}
                                            className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                        >
                                            যোগ করুন
                                        </button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div className="space-y-1">
                            <div className="flex items-center gap-2 px-2">
                                <span className="flex-1 min-w-0 text-xs font-medium text-zinc-500 dark:text-zinc-400">সেবার নাম</span>
                                <span className="w-12 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400">পরিমাণ</span>
                                <span className="w-16 text-right text-xs font-medium text-zinc-500 dark:text-zinc-400">দাম</span>
                                <span className="flex-shrink-0 w-7" />
                            </div>
                            {items.map((item) => (
                                <div key={item.id} className="flex items-center gap-1.5 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg border border-zinc-200 dark:border-zinc-700/60 p-1.5">
                                    <div className="flex-1 min-w-0">
                                        <GeoCombobox
                                            value={item.name}
                                            onChange={val => updateItem(item.id, 'name', sanitizeText(val))}
                                            onSelectOption={opt => { if (opt.price != null) updateItem(item.id, 'price', String(opt.price)); }}
                                            onClear={() => updateItem(item.id, 'price', '')}
                                            options={serviceOptions}
                                            clearable
                                            placeholder="সেবার নাম"
                                            className="pl-2.5 py-1 border-zinc-200 dark:border-zinc-700 rounded-lg text-zinc-800 focus:ring-primary/40"
                                        />
                                    </div>
                                    <input type="text" inputMode="numeric" placeholder="পরিমাণ" value={item.qty} onChange={e => updateItem(item.id, 'qty', e.target.value)} className="w-12 px-2 py-1 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm text-center bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                    <input type="text" inputMode="numeric" placeholder="দাম" value={item.price} onChange={e => updateItem(item.id, 'price', e.target.value)} className="w-16 px-2 py-1 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm text-right bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40" />
                                    <button type="button" onClick={() => removeItem(item.id)} disabled={items.length === 1} className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-30 transition-colors">
                                        <IconX size={13} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        {/* Bottom add button — quick to add another row without scrolling up. */}
                        <button type="button" onClick={addItem} className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-orange-200 dark:border-orange-800/40 text-orange-600 dark:text-orange-400 text-xs font-semibold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors">
                            <IconPlus size={14} />
                            সেবা যোগ
                        </button>
                    </div>

                    {/* Discount & Deposit */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">ছাড় (৳)</label>
                            <input type="text" inputMode="numeric" placeholder="০" value={discount} onChange={e => setDiscount(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">জমা (৳)</label>
                            <input type="text" inputMode="numeric" placeholder="০" value={deposit} onChange={e => { depositTouchedRef.current = true; setDeposit(e.target.value); }} className={inputCls} />
                        </div>
                    </div>

                    {/* Invoice status — derived from জমা; the buttons set জমা to match */}
                    <div>
                        <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">ইনভয়েসের অবস্থা</label>
                        <div className="flex items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={pickPaid}
                                disabled={!canPickPaid}
                                title={!canPickPaid ? 'জমা সর্বমোটের সমান বা বেশি হলে পরিশোধিত করা যাবে' : undefined}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${isPaid ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-emerald-400'} ${!canPickPaid ? 'opacity-40 cursor-not-allowed hover:border-zinc-200' : ''}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${isPaid ? 'bg-white' : 'bg-emerald-500'}`} />
                                পরিশোধিত
                            </button>
                            <button
                                type="button"
                                onClick={pickDue}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors ${!isPaid ? 'bg-red-500 border-red-500 text-white' : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-red-400'}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${!isPaid ? 'bg-white' : 'bg-red-500'}`} />
                                বকেয়া
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right: live preview of the selected invoice template. On desktop it sits
                    beside the form; on mobile it drops below the form (second grid cell) so
                    the user can still see the rendered invoice. InvoicePreviewFrame auto-scales
                    its design width to the container, so it fits phone widths cleanly. Shown
                    from the start so the chosen theme is visible immediately. */}
                <div className="block border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 shadow-lg shadow-zinc-200/50 dark:shadow-black/20">
                    <div className="mx-auto" style={paperPx(invoiceSettings?.paperSize).w < 500 ? { maxWidth: paperPx(invoiceSettings?.paperSize).w } : undefined}>
                        <InvoicePreviewFrame html={livePreviewHtml} paper={paperPx(invoiceSettings?.paperSize)} reloadKey={`${selectedTemplate}|${invoiceSettings?.paperSize}`} />
                    </div>
                </div>
            </div>
        </div>
    );
}
